import React, { useEffect, useRef } from "react";
import { Navigation, Clock } from "lucide-react";
import { toast } from "sonner";
import RideMap from "./RideMap";
import useDriverPosition from "./useDriverPosition";
import useRoadRoute from "./useRoadRoute";
import TrafficLegend from "./TrafficLegend";
import { titleCase } from "../../lib/api";

// Map for an active ride with a smoothly moving driver marker, the real Google
// driving route drawn on the roads (coloured by live traffic), and a live ETA badge.
export default function LiveRideMap({ ride, drivers, className = "" }) {
  const { pos, segment, remainingKm, progress } = useDriverPosition(ride);
  const active = ride && !["cancelled"].includes(ride.status);
  const driver = active && ride.driver && pos ? { ...ride.driver, lat: pos.lat, lng: pos.lng } : null;
  const pickup = { lat: ride.pickup_lat, lng: ride.pickup_lng };
  const drop = { lat: ride.drop_lat, lng: ride.drop_lng };

  const target =
    ride.status === "in_progress"
      ? drop
      : ["driver_assigned", "driver_arriving", "searching"].includes(ride.status)
        ? pickup
        : null;

  // Route fetch uses a STABLE origin (backend-reported driver position, else pickup) so the
  // fast-animating marker position doesn't thrash the Routes request; the marker still uses `pos`.
  const stableOrigin =
    ride.driver && Number.isFinite(ride.driver.lat)
      ? { lat: ride.driver.lat, lng: ride.driver.lng }
      : ride.status === "in_progress"
        ? pickup
        : pos;
  const road = useRoadRoute(stableOrigin, target, active && ride.status !== "completed" && !!stableOrigin && !!target);
  const routePts = road.points.length >= 2 ? road.points : target && pos ? [pos, target] : null;

  // Prefer Google's traffic-aware duration; fall back to a hill-town average (~24 km/h).
  const etaMin = road.durationMin != null ? Math.max(1, Math.round(road.durationMin)) : Math.max(1, Math.ceil(remainingKm / 0.4));
  const kmLeft = road.distanceKm != null ? road.distanceKm : remainingKm;

  // Arrival alert — one-time nudge per segment when the driver is ~1 min away.
  const alertedRef = useRef("");
  useEffect(() => {
    if (!active || ride.status === "completed" || !driver) return;
    const segKey = `${ride.ride_id}-${ride.status}`;
    if (etaMin <= 1 && alertedRef.current !== segKey) {
      alertedRef.current = segKey;
      toast.success(ride.status === "in_progress" ? "Almost at your destination!" : `${ride.driver?.name || "Your driver"} is about a minute away!`);
    }
  }, [etaMin, ride?.status, ride?.ride_id, active]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`relative ${className}`} data-testid="live-ride-map">
      <RideMap pickup={pickup} drop={drop} activeDriver={driver} drivers={drivers} route={routePts} traffic={road.traffic} follow className="h-full min-h-[420px]" />
      {active && ride.status !== "completed" && driver && (
        <div className="absolute left-4 top-4 z-[500] flex items-center gap-3 rounded-2xl bg-white/95 p-3 pr-4 shadow-lg shadow-ink/10 backdrop-blur" data-testid="live-eta">
          <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white kt-pulse-ring"><Navigation className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-semibold text-ink">{ride.driver.name} · {segment?.label === "to pickup" ? "heading to you" : "on the way to drop"}</p>
            <p className="inline-flex items-center gap-1 text-xs text-ink-soft" data-testid="live-eta-text"><Clock className="h-3 w-3" /> ~{etaMin} min · {kmLeft.toFixed(1)} km left · {titleCase(ride.status)}</p>
          </div>
        </div>
      )}
      {active && (
        <div className="absolute inset-x-4 bottom-4 z-[500] h-1.5 overflow-hidden rounded-full bg-white/70"><div className="h-full rounded-full bg-brand transition-[width] duration-700" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      )}
      {active && road.traffic.length > 0 && (
        <div className="absolute right-4 top-4 z-[500]"><TrafficLegend /></div>
      )}
    </div>
  );
}
